import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";

@Component({
  selector: "palliative-card",
  templateUrl: "palliative-card.html",
})
export class PalliativeCardComponent implements OnInit {
  @Input() assessmentId: string;
  @Input() config;
  @Input() sectionId: string;
  @Input() disabled: boolean = false;
  @Input() isPALToggle: boolean;

  @Output() onDataUpdate: EventEmitter<any> = new EventEmitter();
  @Output() onCardInitialized: EventEmitter<any> = new EventEmitter();

  id: string;
  public palliativeToggle: boolean = false;
  public access: string;
  public cardId: string;
  public componentId: string;
  public name: string;
  public textUpper = false;
  public dataHeader = [];
  public rows = [];
  innerGroup: FormGroup;
  initialized: boolean = false;
  values: object = {};
  selected: string[] = [];
  value = "";
  type = "";
  selectedValues = [];
  valueKey: string;

  constructor(
    public translate: TranslateService,
    public formBuilder: FormBuilder,
    public stateData: StateManagementService
  ) {}

  ngOnInit(): void {
    this.access = this.config.access;
    this.cardId = this.config.id;
    this.componentId = this.config.componentId;
    this.name = this.config.cardName;
    this.dataHeader = this.config.data.dataHeaders;
    this.rows = this.config.billingCodes;
    this.id = this.config.id;
    this.valueKey = this.id + "-" + "billingCard";

    this.innerGroup = this.formBuilder.group({});
    let formState = null;
    this.innerGroup.addControl(
      "billingCode",
      this.formBuilder.control(formState)
    );

    this.rows.forEach((r) => {
      if (r.row) {
        this.innerGroup.addControl(
          r.row[0].fieldName,
          new FormControl({ value: "", disabled: false })
        );
      }
    }, this);

    this.stateData.showPalliativeV1_5.subscribe((x) => {
      if (x !== null) {
        this.palliativeToggle = x;
      }
    });

    this.runValidations();
  }

  runValidations() {
    this.rows.forEach((r) => {
      if (r.row) {
        let fieldName = r.row[0].fieldName;
        let dependencies = r.row[0].dependencies;
        let disables = r.row[0].disables;

        if (dependencies) {
          let dependencyFulfilled = false;

          dependencies.forEach((dependency) => {
            let included = this.selectedValues.includes(dependency);

            if (included) {
              dependencyFulfilled = true;
            }
          }, this);

          if (dependencyFulfilled) {
            this.innerGroup.controls[fieldName].enable();
          } else {
            this.innerGroup.controls[fieldName].disable();
            this.innerGroup.controls[fieldName].setValue(false);
            this.removeSelectedValue(fieldName);
          }
        }

        // If this row disables other rows
        if (disables) {
          // If the card has stored values already
          if (this.values[this.valueKey]) {
            let isSelected = this.values[this.valueKey].includes(fieldName);

            if (isSelected) {
              // If this field is selected, make sure disabled fields are disabled
              this.disableAndRemoveFieldArray(disables);
            } else {
              // If this field is not selected, make sure disabled fields are enabled
              this.enableFieldArray(disables);
            }
          } else {
            // If the card is fresh, enable all "disabled-by" fields
            this.enableFieldArray(disables);
          }
        }
      }
    });
  }

  @Input() set data(card: any) {
    if (card) {
      if (card.data) {
        // Set values
        this.values = card.data[0];

        if (Array.isArray(this.values[this.valueKey])) {
          this.selectedValues = this.values[this.valueKey];

          if (this.selectedValues.length > 0) {
            this.rows.forEach((row, index) => {
              const rowData = this.rows[index].row;

              if (rowData == undefined) return;

              let fieldName = rowData[0].fieldName;
              let fieldValue = rowData[0].value;

              // If the row has a selected value matching the current field name
              if (this.selectedValues.indexOf(fieldName, 0) != -1) {
                fieldValue = fieldName;

                // Mark the checkbox as checked
                this.innerGroup.controls[fieldName].setValue(true);
              }
            });

            this.runValidations();
          }
        } else {
          this.innerGroup.controls["billingCode"].setValue(
            this.values[this.valueKey]
          );
        }
      }
    }

    if (card !== undefined && !this.initialized) {
      this.initialized = true;
      this.onCardInitialized.emit(this.id);
    }
  }

  dataUpdate(data) {
    this.values[this.valueKey] = data.target.getAttribute("ng-reflect-value");
    this.onDataUpdate.emit({ cardId: this.id, value: this.values });
  }

  onBillingChange(event) {
    const fieldName = event.target.value;

    if (event.target.checked) {
      this.addSelectedValue(fieldName);
    } else {
      this.removeSelectedValue(fieldName);
    }

    this.runValidations();
    this.updateValuesAndEmit();
  }

  makeControlDisabled() {
    if (this.disabled || this.access == "read") {
      this.innerGroup.controls["billingCode"].disable();
      return true;
    } else {
      return false;
    }
  }

  /**
   * Removes an array of fields from the selected values and disables them
   * @param fields string[]
   * @private
   */
  private disableAndRemoveFieldArray(fields: any[]) {
    fields.forEach((fieldToDisable) => {
      this.removeSelectedValue(fieldToDisable);
      this.innerGroup.controls[fieldToDisable].disable();
      this.innerGroup.controls[fieldToDisable].setValue(false);
    });
  }

  /**
   * Enables an array of field form controls
   * @param fields string[]
   * @private
   */
  private enableFieldArray(fields: any[]) {
    fields.forEach((fieldToDisable) => {
      this.innerGroup.controls[fieldToDisable].enable();
    });
  }

  /**
   * Updates the values array and emits a data update message
   * @private
   */
  private updateValuesAndEmit() {
    this.values[this.valueKey] = this.selectedValues;
    this.onDataUpdate.emit({ cardId: this.id, value: this.values });
  }

  /**
   * Adds a value to the selectedValues array
   * @param value
   * @private
   */
  private addSelectedValue(value) {
    this.selectedValues.push(value);
  }

  /**
   * Splices a value from selectedValues array
   * @param value key to remove from array
   * @private
   */
  private removeSelectedValue(value) {
    const index = this.selectedValues.indexOf(value);
    if (index > -1) {
      this.selectedValues.splice(index, 1);
    }
  }

  /**
   * Clears all selected values from the card
   * @private
   */
  clearSelectedValues() {
    let svCopy = [...this.selectedValues];

    svCopy.forEach((value) => {
      this.removeSelectedValue(value);
      this.innerGroup.controls[value].setValue(false);
    });

    this.runValidations();
    this.updateValuesAndEmit();

    this.innerGroup.controls["billingCode"].setValue(false);
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/palliative-card/palliative-card.ts